import { Fragment, useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { X, ChevronLeft, ChevronRight, ExternalLink, ImageOff, Download } from "lucide-react";
import { cn } from "@/lib/utils";

export type AttachmentsGalleryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  imageUrls: string[];
  initialIndex?: number;
  alt?: string;
  comprobanteIndex?: number;
};

function getImageLabel(index: number, comprobanteIndex: number | undefined): string | null {
  if (comprobanteIndex === undefined) return null;
  if (comprobanteIndex === -1) return `Anexo ${index + 1}`;
  if (index === comprobanteIndex) return "Comprobante";
  let anexoNum = 0;
  for (let j = 0; j <= index; j++) if (j !== comprobanteIndex) anexoNum++;
  return `Anexo ${anexoNum}`;
}

export function AttachmentsGalleryModal({
  isOpen,
  onClose,
  imageUrls,
  initialIndex = 0,
  alt = "Comprobante de pago",
  comprobanteIndex,
}: AttachmentsGalleryModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [loadError, setLoadError] = useState(false);

  const count = imageUrls.length;
  const currentUrl = count > 0 ? imageUrls[currentIndex] ?? null : null;
  const hasPrev = count > 1 && currentIndex > 0;
  const hasNext = count > 1 && currentIndex < count - 1;
  const currentLabel = getImageLabel(currentIndex, comprobanteIndex);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(Math.min(Math.max(0, initialIndex), Math.max(0, count - 1)));
      setLoadError(false);
    }
  }, [isOpen, initialIndex, count]);

  const goPrev = useCallback(() => {
    if (hasPrev) {
      setCurrentIndex((i) => i - 1);
      setLoadError(false);
    }
  }, [hasPrev]);

  const goNext = useCallback(() => {
    if (hasNext) {
      setCurrentIndex((i) => i + 1);
      setLoadError(false);
    }
  }, [hasNext]);

  const openInNewTab = useCallback(() => {
    if (currentUrl) {
      const link = document.createElement("a");
      link.href = currentUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
    }
  }, [currentUrl]);

  const [isDownloading, setIsDownloading] = useState(false);
  const downloadComprobante = useCallback(async () => {
    if (!currentUrl) return;
    setIsDownloading(true);
    try {
      const res = await fetch(currentUrl);
      const blob = await res.blob();
      const ext = currentUrl.split(/[#?]/)[0].match(/\.(jpe?g|png|gif|webp|pdf)$/i)?.[1] ?? "jpg";
      const filename = count > 1 ? `comprobante-pago-${currentIndex + 1}.${ext}` : `comprobante-pago.${ext}`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsDownloading(false);
    }
  }, [currentUrl, count, currentIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, goPrev, goNext]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            aria-hidden
            onClick={onClose}
          />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto p-4">
          <div className="flex min-h-full items-center justify-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                className="relative w-full max-w-4xl transform"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    {hasPrev && (
                      <button
                        type="button"
                        onClick={goPrev}
                        className="rounded-lg bg-white/90 dark:bg-zinc-800 p-2 text-slate-600 dark:text-slate-300 shadow-lg hover:bg-white dark:hover:bg-zinc-700 transition-colors"
                        aria-label="Comprobante anterior"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                    )}
                    <div className="flex flex-col items-start gap-0.5">
                      {currentLabel != null && (
                        <span className="text-xs font-medium text-white/90 uppercase tracking-wide">
                          {currentLabel}
                        </span>
                      )}
                      <span className="text-sm font-medium text-white bg-black/30 dark:bg-white/10 px-3 py-1.5 rounded-lg">
                        {count > 0 ? `${currentIndex + 1} / ${count}` : "0 / 0"}
                      </span>
                    </div>
                    {hasNext && (
                      <button
                        type="button"
                        onClick={goNext}
                        className="rounded-lg bg-white/90 dark:bg-zinc-800 p-2 text-slate-600 dark:text-slate-300 shadow-lg hover:bg-white dark:hover:bg-zinc-700 transition-colors"
                        aria-label="Siguiente comprobante"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {currentUrl && (
                      <>
                        <button
                          type="button"
                          onClick={downloadComprobante}
                          disabled={isDownloading}
                          className="flex items-center gap-2 rounded-lg bg-white/90 dark:bg-zinc-800 px-3 py-2 text-slate-600 dark:text-slate-300 shadow-lg hover:bg-white dark:hover:bg-zinc-700 transition-colors text-sm font-medium disabled:opacity-50"
                        >
                          <Download className="h-4 w-4 shrink-0" />
                          {isDownloading ? "Descargando…" : "Descargar comprobante"}
                        </button>
                        <button
                          type="button"
                          onClick={openInNewTab}
                          className="flex items-center gap-2 rounded-lg bg-white/90 dark:bg-zinc-800 px-3 py-2 text-slate-600 dark:text-slate-300 shadow-lg hover:bg-white dark:hover:bg-zinc-700 transition-colors text-sm font-medium"
                        >
                          <ExternalLink className="h-4 w-4 shrink-0" />
                          Abrir en nueva pestaña
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-full bg-white/90 dark:bg-zinc-800 p-2 text-slate-600 dark:text-slate-300 shadow-lg hover:bg-white dark:hover:bg-zinc-700 transition-colors"
                      aria-label="Cerrar"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {count === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-3 rounded-lg bg-slate-800/50 dark:bg-zinc-900/80 py-16 px-8 text-slate-400">
                    <ImageOff className="h-12 w-12 opacity-60" />
                    <p className="text-sm">No hay comprobantes para mostrar</p>
                  </div>
                ) : currentUrl && !loadError ? (
                  <img
                    src={currentUrl}
                    alt={`${alt} ${currentIndex + 1}`}
                    className="w-full h-auto max-h-[75vh] object-contain rounded-lg shadow-2xl bg-slate-900/50"
                    draggable={false}
                    onError={() => setLoadError(true)}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center gap-3 rounded-lg bg-slate-800/50 dark:bg-zinc-900/80 py-16 px-8 text-slate-400">
                    <ImageOff className="h-12 w-12 opacity-60" />
                    <p className="text-sm">
                      No se pudo cargar este comprobante. Use &quot;Abrir en nueva pestaña&quot; para intentar.
                    </p>
                  </div>
                )}

                {count > 1 && count <= 10 && (
                  <div className="flex flex-wrap justify-center gap-1.5 mt-3">
                    {imageUrls.map((url, index) => (
                      <button
                        key={`${url}-${index}`}
                        type="button"
                        onClick={() => {
                          setCurrentIndex(index);
                          setLoadError(false);
                        }}
                        className={cn(
                          "w-12 h-12 rounded-lg overflow-hidden border-2 shrink-0 bg-slate-700",
                          index === currentIndex
                            ? "border-green-500 ring-2 ring-green-500/50"
                            : "border-transparent opacity-70 hover:opacity-100"
                        )}
                      >
                        <img
                          src={url}
                          alt=""
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
